import { useCallback, useEffect, useRef } from 'react';
import { type CancelError } from '@atlassian/search-client/cancellable-promise';
import { type UFOExperience } from '@atlassian/ufo';

import { EMPTY_SEARCH_ITEMS } from '../product-router/product/result-provider/result-provider-types';
import { type SearchItems } from '../product-router/product/result-types';
import useCancellablePromiseOnUnmount from '../utils/use-cancelled-promise-on-un-mount';
import useMaxOneInflightRequest from '../utils/use-max-one-inflight-request';
import { APIStates } from './product-state-machine-types';
import { useQuery } from '../query-context/query-context';
import { hasSearchResults } from '../product-router/product/utils';
import { useGenericFilters } from '../filters/filter-context/filter-context';
import { useTypedAggregatorClient } from '../aggregator-client-context/aggregator-client-context';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';

type SetAPIState = (value: APIStates) => void;
export interface DecoratorResultSetStateProps<A> {
	// The response returned from the supplier is stored into a state so that the UI
	// can update itself.
	setState: (arg: SearchItems) => void;
	// Tracks the status of the search item supplier.
	setAPIState: SetAPIState;
	// This callback generates results as per the standarised search results interface.
	searchItemSupplier: (arg: A) => Promise<SearchItems>;
}

/**
 * This decorator accepts a simple search result supplier (pre/post) and decorates it with the
 * functionality of setting the results and api states depending on the different kinds of responses
 * that can be obtained via the supplier.
 *
 * @returns a decorated function that sets the results and api in their corresponding states.
 * Note: Setting these separately in different ticks was causing the results to be stale.
 */
export const useDecorateWithResultAndAPISetState: <A>(
	args: DecoratorResultSetStateProps<A> & {
		id: string;
		ufoExperience: UFOExperience;
		reactUFOExperience?: string;
		debounceTime?: number;
	},
) => (arg: A) => Promise<SearchItems> = ({
	id,
	setState,
	searchItemSupplier,
	setAPIState,
	ufoExperience,
	reactUFOExperience,
	debounceTime,
}) => {
	const generateCancelledPromiseOnunmount = useCancellablePromiseOnUnmount();
	const generateMaxOneInflightRequest = useMaxOneInflightRequest();
	const { query } = useQuery();
	const { filterDefinitions } = useGenericFilters();
	const presentQuery = useRef(query);
	const presentFilters = useRef(filterDefinitions);
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId || '';

	useEffect(() => {
		presentQuery.current = query;
		presentFilters.current = filterDefinitions;
	}, [query, filterDefinitions]);

	return useCallback(
		(arg: Parameters<typeof searchItemSupplier>[0]) => {
			return new Promise((resolve, reject) => {
				if (reactUFOExperience) {
					traceUFOPress(`${id}-${reactUFOExperience}`, performance.now() - (debounceTime || 0));
				}
				setAPIState(APIStates.Loading);
				ufoExperience.start();
				generateMaxOneInflightRequest(generateCancelledPromiseOnunmount(searchItemSupplier(arg)))
					.then((response: SearchItems) => {
						if (presentQuery.current !== query) {
							ufoExperience.abort();
							return;
						}
						ufoExperience.success({
							metadata: {
								cloudId,
							},
						});
						setState(response);
						if (hasSearchResults(response)) {
							setAPIState(APIStates.Success);
						} else {
							setAPIState(APIStates.NoResult);
						}
						resolve(response);
					})
					.catch((e) => {
						if (presentQuery.current !== query) {
							ufoExperience.abort();
							return;
						}
						/**
						 * Set state only if the promise was not cancelled, which happens only to avoid multiple calls going out.
						 */
						if (e && !(e as CancelError).isCancelled) {
							setState(EMPTY_SEARCH_ITEMS);
							setAPIState(APIStates.Error);
							const errorName = typeof (e as Error).name === 'string' ? (e as Error).name : 'Error';
							const errorMessage =
								typeof (e as Error).message === 'string' ? (e as Error).message : 'request failed';
							const err = e as Error & { status?: number; response?: { status?: number } };
							ufoExperience.failure({
								metadata: {
									cloudId,
									productId: id,
									errorName,
									errorMessage: errorMessage.slice(0, 500),
									statusCode: err.status ?? err.response?.status,
								},
							});

							if (reactUFOExperience) {
								setInteractionError(`${id}-${reactUFOExperience}`, {
									errorMessage,
									name: errorName,
								});
							}

							reject(e);
						} else if ((e as CancelError).isCancelled) {
							ufoExperience.abort();
						}
					});
			});
		},
		[
			reactUFOExperience,
			setAPIState,
			ufoExperience,
			generateMaxOneInflightRequest,
			generateCancelledPromiseOnunmount,
			searchItemSupplier,
			id,
			debounceTime,
			query,
			cloudId,
			setState,
		],
	);
};
